import Button from '@/components/UI/Button/Button';
import InputWithUnits from '@/components/UI/FormElements/InputWithUnits';
import { preventNumberScroll } from '@/helpers';
import type { RepeatUnit, TaskScheduleConditionalType } from '@/model/Task.model';
import { type ChangeEvent, type FC, useMemo, useState } from 'react';
import {
  countRecurringPattern,
  generateRecurringPattern,
  RECURRING_PATTERN_MAX_DAYS,
} from './AddTaskScheduleConditionalFields.utils';
import { repeatPeriodOptions } from './AddTaskScheduleForm.utils';
import styles from './RecurringPatternGenerator.module.scss';

interface RecurringPatternGeneratorProps {
  type: TaskScheduleConditionalType | undefined;
  onApply: (days: Array<number>) => void;
}

const dayLabel = (type: TaskScheduleConditionalType | undefined): string => {
  switch (type) {
    case 'age':
      return 'age';
    case 'tracking_day':
      return 'tracking day';
    case 'study_day':
    default:
      return 'study day';
  }
};

const parseInput = (event: ChangeEvent<HTMLInputElement>): number | null => {
  const raw = event.target.value;
  if (raw === '') {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const RecurringPatternGenerator: FC<RecurringPatternGeneratorProps> = ({ type, onApply }) => {
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const [every, setEvery] = useState<number | null>(null);
  const [everyUnit, setEveryUnit] = useState<RepeatUnit>('days');

  const generated = useMemo(
    () => generateRecurringPattern({ from, to, every, everyUnit, type }),
    [from, to, every, everyUnit, type]
  );
  const totalCount = useMemo(
    () => countRecurringPattern({ from, to, every, everyUnit, type }),
    [from, to, every, everyUnit, type]
  );

  const exceedsMax = totalCount > RECURRING_PATTERN_MAX_DAYS;
  const fromText = from === null ? '-' : `${from}`;
  const toText = to === null ? '-' : `${to}`;
  const everyText = every === null ? '-' : `${every}`;
  const countText = totalCount === 0 ? '-' : `${totalCount}`;
  const everyUnitLabel = everyUnit === 'weeks' ? 'week(s)' : 'day(s)';

  const label = dayLabel(type);

  return (
    <div className={styles.container} data-test-component="RecurringPatternGenerator" data-test-element="container">
      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="recurring-pattern-from">
            From {label}
          </label>
          <input
            id="recurring-pattern-from"
            type="number"
            step={1}
            className={styles.numberInput}
            value={from ?? ''}
            onChange={(event) => setFrom(parseInput(event))}
            onWheel={preventNumberScroll}
            data-testid="recurring-pattern-from"
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="recurring-pattern-to">
            To {label}
          </label>
          <input
            id="recurring-pattern-to"
            type="number"
            step={1}
            className={styles.numberInput}
            value={to ?? ''}
            onChange={(event) => setTo(parseInput(event))}
            onWheel={preventNumberScroll}
            data-testid="recurring-pattern-to"
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Repeat every</span>
          <InputWithUnits
            name="recurring-pattern-every"
            classes={styles.everyInput}
            value={every ?? undefined}
            unit={everyUnit}
            unitOptions={Object.values(repeatPeriodOptions)}
            valueMin={1}
            valueStep={1}
            onChange={({ value, unit }) => {
              setEvery(value === undefined || Number.isNaN(value) ? null : value);
              setEveryUnit(unit as RepeatUnit);
            }}
          />
        </div>
      </div>
      <div className={styles.previewRow}>
        <p className={styles.preview} data-testid="recurring-pattern-preview">
          Preview: From {label} {fromText} to {toText}, every {everyText} {everyUnitLabel} = {countText} task
          {totalCount === 1 ? '' : 's'}
          {exceedsMax ? ` (exceeds max of ${RECURRING_PATTERN_MAX_DAYS})` : ''}
        </p>
        <Button
          primary
          onClick={() => onApply(generated)}
          disabled={generated.length === 0 || exceedsMax}
          testKey="recurring-pattern-apply"
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

export default RecurringPatternGenerator;
